import googleAdsIcon from '@/assets/settings/app-logos/google-ads.svg';
import googleSearchConsoleIcon from '@/assets/settings/app-logos/google-search-console.svg';
import { describeCron } from '@/utils/scheduleCron';

export interface RoutineSuggestion {
  label: string;
  prompt: string;
  icon?: string;
}

export interface FrequencyOption {
  label: string;
  value: string;
  cron: string;
}

/**
 * Empty-state copy for a create-routine thread. The thread opens with the
 * skill attached but nothing sent, so this replaces the lobby's time greeting:
 * what the thread is for, plus chips that prefill a worked example.
 */
export const CREATE_ROUTINE_EMPTY_STATE_SUBTITLE =
  "Describe what should happen and how often it should run. I'll turn it into a routine.";

/**
 * Empty-state copy for an edit-routine thread. The thread opens with nothing
 * sent, so this is what the user lands on: the routine named in the greeting,
 * and three chips that prefill the composer with the change they want.
 */
export const EDIT_ROUTINE_EMPTY_STATE_SUBTITLE =
  'Ask a question, make a change, or explore how this routine is working.';

export const EDIT_ROUTINE_STARTER_PROMPTS: string[] = [
  'Update alert logic',
  'Update run schedule',
  'Update alert channels',
];

/**
 * Starter routines offered as chips. The first three also seed the homepage
 * Inbox's "Recommended" list (`RECOMMENDED_ROUTINE_COUNT`), so append rather
 * than reorder when adding to this list.
 */
export const ROUTINE_SUGGESTIONS: RoutineSuggestion[] = [
  {
    label: 'Daily spend anomaly',
    prompt: 'Monitor Google Ads daily spend and alert when the current day\'s spend deviates by more than 40% from the trailing 7-day average.',
    icon: googleAdsIcon,
  },
  {
    label: 'Conversion tracking break',
    prompt: 'Monitor for a conversion tracking break: alert when all active Google Ads campaigns show 0 conversions for 2 consecutive days (48 hours)',
    icon: googleAdsIcon,
  },
  {
    label: 'Conversion rate drop',
    prompt: 'Alert when campaign conversion rate (conversions ÷ clicks) drops > 20% vs previous 7-day period',
    icon: googleAdsIcon,
  },
  {
    label: 'Homepage query drop',
    prompt: 'Alert when a United States search query on our home page drops by more than 5 clicks and its average position worsens by more than 2 points compared with the previous day.',
    icon: googleSearchConsoleIcon,
  },
  {
    label: 'Weekly SEO summary',
    prompt: 'Every Monday, summarise last week\'s Google Search Console performance — clicks, impressions and average position against the previous week — and call out the queries and pages that moved the most.',
    icon: googleSearchConsoleIcon,
  },
  {
    label: 'ROAS comparison',
    prompt: 'Every week, compare return on ad spend across active Google Ads campaigns against the previous week and flag the campaigns whose ROAS moved by more than 15%.',
    icon: googleAdsIcon,
  },
];

export const FREQUENCY_OPTIONS: FrequencyOption[] = [
  { label: '1h', value: '1h', cron: '0 * * * *' },
  { label: '4h', value: '4h', cron: '0 */4 * * *' },
  { label: '6h', value: '6h', cron: '0 */6 * * *' },
  { label: '12h', value: '12h', cron: '0 */12 * * *' },
  { label: 'Daily', value: 'daily', cron: '0 9 * * *' },
  { label: 'Weekly', value: 'weekly', cron: '0 9 * * 1' },
];

/**
 * Human label for a routine's cron ("0 9 * * *" → "Daily"). Schedules created
 * by the in-chat schedule card carry arbitrary times/day sets ("30 9 * * 1-5"),
 * so unknown crons are humanized via `describeCron` ("Weekdays · 9:30 AM")
 * before falling back to the raw cron. Prefer {@link describeRoutineCadence}
 * for display — it keeps the time of day where this collapses it.
 */
export function getFrequencyLabel(cron: string): string {
  const match = FREQUENCY_OPTIONS.find((opt) => opt.cron === cron);
  return match?.label ?? describeCron(cron) ?? cron;
}

/**
 * A routine's cadence in words, e.g. "Weekly on Mon, Tue · 9:00 AM" or "4h".
 *
 * `describeCron` comes first because it keeps the time of day;
 * {@link getFrequencyLabel} collapses the legacy picker's crons to a bare
 * "Daily"/"4h", so it only serves as the fallback for the every-N-hours shapes
 * `describeCron` can't read. Shared by the routine detail page's facts card and
 * the chat preview panel so both name a cadence the same way.
 */
export function describeRoutineCadence(cron: string): string {
  return describeCron(cron) ?? getFrequencyLabel(cron);
}

/** Whether the routine carries a real stored title — legacy rows have none. */
export function hasRoutineTitle(routine: { title?: string | null }): boolean {
  return Boolean(routine.title && routine.title.trim());
}

/**
 * What to call a routine on screen: its title, or the description standing in
 * for it on a titleless row. Surfaces that style the two differently (the
 * listing row and the detail heading keep the description muted, so a titleless
 * routine reads as a description rather than a headline) pair this with
 * {@link hasRoutineTitle}.
 */
export function routineDisplayName(routine: {
  title?: string | null;
  description: string;
}): string {
  return hasRoutineTitle(routine) ? routine.title! : routine.description;
}
